import { computed, ref, watch } from 'vue';
import type { Router } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import {
  createRoleRequest,
  fetchMyRoleRequestOptions,
} from '@/services/roleRequests';
import type { RoleRequestOptions } from '@/types/roleRequest';
import { sortUserTypes } from '@/utils/userTypeStyle';
import { useAsyncAction } from '@/composables/useAsyncAction';

const options = ref<RoleRequestOptions | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const modalOpen = ref(false);
/** Cierra el modal solo hasta el próximo inicio de sesión o recarga de página */
const closedForCurrentVisit = ref(false);
let loadPromise: Promise<void> | null = null;
let promptInitialized = false;
let appRouter: Router | null = null;

const { loading: submitting, run: runSubmit } = useAsyncAction(async (
  requestedUserTypeId: number,
  specialtyIds: number[],
) => {
  await createRoleRequest({
    requested_user_type_id: requestedUserTypeId,
    specialty_ids: specialtyIds,
  });
  closedForCurrentVisit.value = false;
  modalOpen.value = false;
  await loadOptions(true);
  return true;
});

function isAuthRoute() {
  return appRouter ? !appRouter.currentRoute.value.meta.guest : true;
}

function isDefaultRoleUser() {
  const authStore = useAuthStore();
  return authStore.isAuthenticated && authStore.user?.user_type?.code === 'DEFAULT';
}

function shouldAutoOpenModal() {
  const authStore = useAuthStore();
  return authStore.isAuthenticated
    && authStore.user?.user_type?.code === 'DEFAULT'
    && Boolean(options.value?.can_submit)
    && !closedForCurrentVisit.value;
}

function openModalIfEligible() {
  if (!isAuthRoute()) return;
  if (shouldAutoOpenModal()) {
    modalOpen.value = true;
  }
}

async function loadOptions(force = false) {
  const authStore = useAuthStore();

  if (!isDefaultRoleUser()) {
    options.value = null;
    modalOpen.value = false;
    return;
  }

  if (loadPromise && !force) {
    await loadPromise;
    openModalIfEligible();
    return;
  }

  loading.value = true;
  error.value = null;

  loadPromise = (async () => {
    try {
      options.value = await fetchMyRoleRequestOptions();
      if (options.value?.request?.status === 'approved') {
        await authStore.refreshUser();
      }
      if (!options.value?.can_submit) {
        modalOpen.value = false;
      } else {
        openModalIfEligible();
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      error.value = message || 'No se pudo cargar el estado de tu solicitud de rol.';
      options.value = null;
    } finally {
      loading.value = false;
      loadPromise = null;
    }
  })();

  await loadPromise;
}

async function promptAfterAuthNavigation() {
  if (!isDefaultRoleUser()) return;
  closedForCurrentVisit.value = false;
  await loadOptions(true);
}

/** Registrar una sola vez: abre el modal tras login/verify sin recargar la página */
export function initRoleRequestPrompt(router: Router) {
  if (promptInitialized) return;
  promptInitialized = true;
  appRouter = router;

  const authStore = useAuthStore();

  watch(
    () => authStore.authSessionId,
    async (sessionId, previousSessionId) => {
      if (sessionId === 0 || sessionId === previousSessionId) return;
      if (!authStore.isAuthenticated) return;
      closedForCurrentVisit.value = false;
      await loadOptions(true);
    },
  );

  watch(
    () => authStore.isAuthenticated,
    (authenticated) => {
      if (authenticated) return;
      modalOpen.value = false;
      options.value = null;
      closedForCurrentVisit.value = false;
      loadPromise = null;
    },
  );

  router.afterEach(async (to, from) => {
    if (to.meta.guest) return;
    if (!authStore.isAuthenticated) return;
    // Solo recargar opciones si cambió la ruta principal (evita ráfagas de API).
    if (from.meta.guest || to.path === from.path) return;
    await promptAfterAuthNavigation();
  });
}

export function useRoleRequest() {
  const authStore = useAuthStore();

  const isDefaultRole = computed(() => authStore.user?.user_type?.code === 'DEFAULT');

  const currentRequest = computed(() => options.value?.request ?? null);

  const isPending = computed(() => currentRequest.value?.status === 'pending');

  const isRejected = computed(() => currentRequest.value?.status === 'rejected');

  const canSubmit = computed(() => Boolean(options.value?.can_submit));

  const reviewDaysHint = computed(() => options.value?.review_days_hint ?? 3);

  const requestableUserTypes = computed(() =>
    sortUserTypes(options.value?.user_types ?? []),
  );

  const specialtyCatalog = computed(() => options.value?.specialties ?? []);

  function openModal() {
    if (!canSubmit.value) return;
    closedForCurrentVisit.value = false;
    modalOpen.value = true;
  }

  function closeModalForVisit() {
    closedForCurrentVisit.value = true;
    modalOpen.value = false;
  }

  async function submitRequest(requestedUserTypeId: number, specialtyIds: number[]) {
    if (submitting.value) return false;
    error.value = null;
    try {
      await runSubmit(requestedUserTypeId, specialtyIds);
      return true;
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
      const message = response?.data?.error;
      if (response?.status === 429 || message?.toLowerCase().includes('too many role requests')) {
        error.value = 'RATE_LIMIT';
      } else {
        error.value = message || 'No se pudo enviar la solicitud.';
      }
      return false;
    }
  }

  return {
    options,
    loading,
    submitting,
    error,
    modalOpen,
    isDefaultRole,
    currentRequest,
    isPending,
    isRejected,
    canSubmit,
    reviewDaysHint,
    requestableUserTypes,
    specialtyCatalog,
    loadOptions,
    openModal,
    openModalIfEligible,
    closeModalForVisit,
    submitRequest,
  };
}
